import { Userdata } from '@app/common';
import { clearIosBadgeNumber, registerNotificationClickEvents, scheduleNotifications } from '../CordovaService/Notifications';
import { applyFatalDbError, applyUserdataChangeFromDb, setConnectionState, setUserStartDate } from '../../store/globalStateSlice';
import { store } from '../../store/store';
import { isIosDevice, isMobileDevice } from '../CordovaService/NetworkConnectionService';
import { checkPermissionLocation, checkPermissionMicrophone, checkPermissionNotification, requestPermissionLocation, requestPermissionMicrophone, requestIosPermissionNotification, requestNotificationPermissionAndroid } from '../CordovaService/PermissionsService';
import { ConnectionState } from '../DBService/DbService';
import { createAndSaveEvent } from '../EventLoggingService/EventLoggingService';
import { syncMediaIfProperNetworkConnection, syncMediaToFileSystem } from '../MediaFileService/MediaFileService';
import { ConfigType } from 'src/data/datatypes';
import { clearStoredLogin } from '../AuthStorageService';
import { DbFatalErrorEventDetail, dbFatalErrorEventName, getDbErrorHttpStatus } from '../DbFatalErrorService';

export const initBeforeLogin = () => {
    console.log('initializing before login...');

    document.addEventListener('onUserUnauthorized', () => {
        console.log('Recognized unauthorized user!');
        clearStoredLogin();
        store.dispatch(setConnectionState(ConnectionState.PASSWORD_CHANGED));
    });

    document.addEventListener(dbFatalErrorEventName, (ev: Event) => {
        const fatalEvent = ev as CustomEvent<DbFatalErrorEventDetail>;
        clearStoredLogin();
        store.dispatch(applyFatalDbError({
            code: fatalEvent.detail.code,
            httpStatus: getDbErrorHttpStatus(fatalEvent.detail.error)
        }));
    });
};

export const initAfterLogin = (user: Userdata | undefined, moduleModifiers: string[], studyConfig: ConfigType | null | undefined) => {
    const userId: string | undefined = user?._id;
    const userStartDate: string | undefined = user?.startDate;
    const userProperties: string[] = user?.userProperties || [];
    const studyName: string | undefined = studyConfig?.name;
    const studyId: string | undefined = studyConfig?._id;
    const setUserStartDateOnInitialLogin: boolean | undefined = studyConfig?.setUserStartDateOnInitialLogin;

    createAndSaveEvent(userId, 'login');
    checkSetStartDateToToday(userId, userStartDate, setUserStartDateOnInitialLogin);
    handleDeviceReady(userId, moduleModifiers, userStartDate, userProperties, studyName);

    if (isMobileDevice()) {
        // register resume and pause for notifications and event-logging
        document.addEventListener('resume', () => {
            createAndSaveEvent(userId, 'resume');
            syncMediaIfProperNetworkConnection(studyId);
            handleRedirectFromNotification();
            clearIosBadgeNumber(); // currently notifications are only about modules, so it's fine to clear all badges on resume, even if the user didn't click the notification
        });
        document.addEventListener('pause', () => {
            createAndSaveEvent(userId, 'pause');
        });

        document.addEventListener('online', () => {
            syncMediaIfProperNetworkConnection(studyId);
        });

        window.open = (cordova as any).InAppBrowser.open;

        syncMediaToFileSystem(studyId);
    }

    document.addEventListener('onUserdataChange', (ev: CustomEvent<Userdata>) => {
        console.log('Applying change in userdata: ', ev.detail);
        store.dispatch(applyUserdataChangeFromDb(ev.detail));

        // resched all notifications
        console.log('Re-scheduling notifications...');
        localStorage.removeItem('moduleNotificationsInitialized');
        scheduleNotifications(moduleModifiers, ev.detail.startDate, userProperties, studyName);
    });
};

const handleDeviceReady = (userId: string | undefined, moduleModifiers: string[], userStartDate: string | undefined, userProperties: string[], studyName: string | undefined) => {
    const execute = async () => {
        console.log('[handleDeviceReady] Starting permission requests...');
        await getAllPermissions();
        console.log('[handleDeviceReady] Permissions complete, scheduling notifications...');
        scheduleNotifications(moduleModifiers, userStartDate, userProperties, studyName);
        registerNotificationClickEvents(userId);
    };

    if (window.cordova) {
        document.addEventListener('deviceready', execute);
    } else {
        if (document.readyState === 'complete') {
            execute();
        } else {
            window.addEventListener('load', execute);
        }
    }
};

const handleRedirectFromNotification = () => {
    const pendingRedirect = localStorage.getItem('pendingRedirect');
    if (pendingRedirect) {
        window.location.reload();
    }
}

async function getAllPermissions(): Promise<void> {
    const isMobile = isMobileDevice();
    const isIos = isIosDevice();

    if (!isMobile) {
        console.warn('[getAllPermissions] Platform is "browser". Notifications disabled.');
        return;
    }

    try {
        console.log('[getAllPermissions] Checking microphone permission...');
        const hasMic = await checkPermissionMicrophone();
        if (!hasMic) {
            const granted = await requestPermissionMicrophone();
            console.log(granted ? 'Mic granted' : 'Mic denied');
        }
    } catch (e) {
        console.warn('Error requesting mic permission:', e);
    }

    try {
        console.log('[getAllPermissions] Checking location permission...');
        const hasLocation = await checkPermissionLocation();
        if (!hasLocation) {
            const granted = await requestPermissionLocation();
            console.log(granted ? 'Location granted' : 'Location denied');
        }
    } catch (e) {
        console.warn('Error requesting location permission:', e);
    }

    try {
        console.log('[getAllPermissions] Checking notification permission...');
        const hasNotification = await checkPermissionNotification();
        if (!hasNotification) {
            if (isIos) {
                const granted = await requestIosPermissionNotification();
                console.log(granted ? 'Notifications granted' : 'Notifications denied');
            } else {
                await requestNotificationPermissionAndroid();
            }
        }
    } catch (e) {
        console.warn('Error requesting notification permission:', e);
    }

    console.log('[getAllPermissions] Notification permission flow complete.');
}

const checkSetStartDateToToday = (userId: string | undefined, userStartDate: string | undefined, setUserStartDateOnInitialLogin: boolean | undefined) => {
    if (!userId) {
        console.error('Cannot set start date to today: userId is undefined');
        return;
    }
    
    if (setUserStartDateOnInitialLogin === true) {
        if (!!userStartDate) {
            console.log('User already has a start date set: ', userStartDate); // it means user logged in before
            return;
        }
        setStartDateToToday();
    }
}

export const getLocalMidnightISO = (): string => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // local midnight

    const pad = (num: number) => num.toString().padStart(2, '0');

    const tzOffset = -today.getTimezoneOffset(); // in minutes
    const sign = tzOffset >= 0 ? '+' : '-';
    const offsetHours = pad(Math.floor(Math.abs(tzOffset) / 60));
    const offsetMinutes = pad(Math.abs(tzOffset) % 60);

    return `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}` +
           `T${pad(today.getHours())}:${pad(today.getMinutes())}:${pad(today.getSeconds())}` +
           `${sign}${offsetHours}:${offsetMinutes}`;
};

const setStartDateToToday = () => {
    try {
        const isoWithOffset = getLocalMidnightISO();
        console.log('Setting user start date to today (local midnight with timezone):', isoWithOffset);
        store.dispatch(setUserStartDate(isoWithOffset));
    } catch (error) {
        console.error('Error setting start date to today:', error);
    }
};
